import { useState, useEffect } from 'react'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Save, Trash2 } from 'lucide-react'
import type { Alert, OIThresholdUnit } from '@/types'
import { useExchangeOptions } from '@/hooks/useExchangeOptions'
import { TagExpressionInput } from '@/components/common/TagExpressionInput'
import { legacyTagsToExpression } from '@/lib/tagExpression'
import { TelegramDestinationSelect } from '@/components/settings/TelegramDestinationSelect'

interface OIAlertEditSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  alert?: Alert | null
  onSave: (alert: Partial<Alert>) => Promise<void>
  onDelete?: (id: string) => Promise<void>
}

const OI_PERIOD_OPTIONS = ['5m', '15m', '30m', '1h', '4h', '24h', 'all']
const VOLUME_PERIOD_OPTIONS = ['1m', '5m', '15m', '30m', '1h', '4h', '1d']

export function OIAlertEditSheet({
  open,
  onOpenChange,
  alert,
  onSave,
  onDelete,
}: OIAlertEditSheetProps) {
  const isNew = !alert?.id
  const exchangeOptions = useExchangeOptions('open_interest')

  // Form state
  const [name, setName] = useState('')
  const [exchanges, setExchanges] = useState<string[]>([])
  const [oiThresholdUnit, setOiThresholdUnit] = useState<OIThresholdUnit>('percent')
  const [oiThresholdValues, setOiThresholdValues] = useState<Record<OIThresholdUnit, string>>({ percent: '5', usdt: '', coins: '' })
  const [oiDirection, setOiDirection] = useState<'increase' | 'decrease' | 'both'>('both')
  const [oiPeriod, setOiPeriod] = useState('5m')
  const [volumePeriod, setVolumePeriod] = useState('1h')
  const [minVolumeUsdt, setMinVolumeUsdt] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [enableTelegram, setEnableTelegram] = useState(true)
  const [enablePushover, setEnablePushover] = useState(false)
  const [telegramChatId, setTelegramChatId] = useState('')
  const [telegramReplyTo, setTelegramReplyTo] = useState('')
  const [tagExpression, setTagExpression] = useState('')
  const [legacyRequired, setLegacyRequired] = useState<string[]>([])
  const [legacyExcluded, setLegacyExcluded] = useState<string[]>([])
  const [ignoreSymbols, setIgnoreSymbols] = useState('')

  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const oiThresholdValue = Number(oiThresholdValues[oiThresholdUnit])
  const isThresholdValid = Number.isFinite(oiThresholdValue) && oiThresholdValue > 0
  const thresholdSuffix = oiThresholdUnit === 'percent' ? '%' : oiThresholdUnit === 'usdt' ? 'USDT' : 'coins'

  // Load alert data into form
  useEffect(() => {
    if (alert) {
      setName(alert.name || '')
      setExchanges(alert.exchanges || [alert.exchange].filter(Boolean))
      const thresholdUnit = alert.oi_threshold_unit || 'percent'
      setOiThresholdUnit(thresholdUnit)
      setOiThresholdValues({
        percent: String(alert.oi_threshold_pct ?? 5),
        usdt: '',
        coins: '',
        [thresholdUnit]: String(alert.oi_threshold_value ?? (thresholdUnit === 'percent' ? alert.oi_threshold_pct ?? 5 : '')),
      })
      setOiDirection(alert.oi_direction || 'both')
      const savedPeriod = alert.oi_period === '1d' ? '24h' : alert.oi_period === '*' ? 'all' : alert.oi_period
      setOiPeriod(OI_PERIOD_OPTIONS.includes(savedPeriod) ? savedPeriod : '5m')
      setVolumePeriod(alert.volume_period || '1h')
      setMinVolumeUsdt(String(alert.filters?.min_period_volume_usdt || ''))
      setEnabled(alert.enabled !== false)
      setEnableTelegram(alert.enable_telegram !== false)
      setEnablePushover(alert.enable_pushover === true)
      setTelegramChatId(alert.telegram?.chat_id || '')
      setTelegramReplyTo(alert.telegram?.reply_to_message_id || '')
      // Use tag_expression if available, otherwise convert from legacy
      const existingExpression = (alert as unknown as Record<string, unknown>).tag_expression as string || ''
      if (existingExpression) {
        setTagExpression(existingExpression)
        setLegacyRequired([])
        setLegacyExcluded([])
      } else {
        const required = alert.required_tags || []
        const excluded = alert.exclude_tags || []
        setLegacyRequired(required)
        setLegacyExcluded(excluded)
        // Auto-convert to expression format
        if (required.length > 0 || excluded.length > 0) {
          setTagExpression(legacyTagsToExpression(required, [], excluded))
        } else {
          setTagExpression('')
        }
      }
      setIgnoreSymbols((alert.ignore || []).join(', '))
    } else {
      // Reset form for new alert
      setName('')
      setExchanges([])
      setOiThresholdUnit('percent')
      setOiThresholdValues({ percent: '5', usdt: '', coins: '' })
      setOiDirection('both')
      setOiPeriod('5m')
      setVolumePeriod('1h')
      setMinVolumeUsdt('')
      setEnabled(true)
      setEnableTelegram(true)
      setEnablePushover(false)
      setTelegramChatId('')
      setTelegramReplyTo('')
      setTagExpression('')
      setLegacyRequired([])
      setLegacyExcluded([])
      setIgnoreSymbols('')
    }
  }, [alert, open])

  const toggleExchange = (exchange: string) => {
    setExchanges((prev) =>
      prev.includes(exchange)
        ? prev.filter((e) => e !== exchange)
        : [...prev, exchange]
    )
  }

  const parseSymbols = (str: string): string[] => {
    return str
      .split(/[,\n]/)
      .map((s) => s.trim().toUpperCase())
      .filter((s) => s.length > 0)
  }

  const handleSave = async () => {
    if (!name.trim() || exchanges.length === 0 || !isThresholdValid) return

    setIsSaving(true)
    try {
      const alertData: Partial<Alert> = {
        ...(alert?.id && { id: alert.id }),
        name: name.trim(),
        exchanges,
        oi_threshold_unit: oiThresholdUnit,
        oi_threshold_value: oiThresholdValue,
        ...(oiThresholdUnit === 'percent' && { oi_threshold_pct: oiThresholdValue }),
        oi_direction: oiDirection,
        oi_period: oiPeriod,
        volume_period: volumePeriod,
        enabled,
        enable_telegram: enableTelegram,
        enable_pushover: enablePushover,
        filters: {
          min_period_volume_usdt: Number(minVolumeUsdt) || 0,
        },
        required_tags: [],  // Deprecated, use tag_expression
        exclude_tags: [],   // Deprecated, use tag_expression
        ignore: parseSymbols(ignoreSymbols),
      }

        // Add tag_expression to alert data
        ; (alertData as Record<string, unknown>).tag_expression = tagExpression

      if (enableTelegram && (telegramChatId || telegramReplyTo)) {
        alertData.telegram = {
          chat_id: telegramChatId,
          reply_to_message_id: telegramReplyTo || undefined,
        }
      }

      await onSave(alertData)
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to save alert:', err)
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!alert?.id || !onDelete) return

    if (!confirm('Are you sure you want to delete this alert?')) return

    setIsDeleting(true)
    try {
      await onDelete(alert.id)
      onOpenChange(false)
    } catch (err) {
      console.error('Failed to delete alert:', err)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isNew ? 'Create OI Alert' : 'Edit OI Alert'}</SheetTitle>
          <SheetDescription>
            Configure Open Interest monitoring thresholds and notification settings.
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-6">
          {/* Basic Settings */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="alert-name">Alert Name</Label>
              <Input
                id="alert-name"
                placeholder="My OI Alert"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="alert-enabled">Enabled</Label>
              <Switch
                id="alert-enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
            </div>
          </div>

          <Separator />

          {/* Exchanges */}
          <div className="space-y-2">
            <Label>Exchanges</Label>
            <div className="flex flex-wrap gap-2">
              {exchangeOptions.map((exchange) => (
                <Badge
                  key={exchange}
                  variant={exchanges.includes(exchange) ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => toggleExchange(exchange)}
                >
                  {exchange}
                </Badge>
              ))}
            </div>
            {exchanges.length === 0 && (
              <p className="text-xs text-destructive">Select at least one exchange</p>
            )}
          </div>

          <Separator />

          {/* Thresholds */}
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="oi-threshold-unit">Measure OI Change In</Label>
              <Select value={oiThresholdUnit} onValueChange={(value) => setOiThresholdUnit(value as OIThresholdUnit)}>
                <SelectTrigger id="oi-threshold-unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percent">Percentage (%)</SelectItem>
                  <SelectItem value="usdt">USDT at average price</SelectItem>
                  <SelectItem value="coins">Coins (absolute change)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="oi-threshold-value">OI Change Threshold ({thresholdSuffix})</Label>
              <Input
                id="oi-threshold-value"
                type="number"
                min="0"
                step="any"
                placeholder={oiThresholdUnit === 'percent' ? '5' : oiThresholdUnit === 'usdt' ? '100000' : '1000'}
                value={oiThresholdValues[oiThresholdUnit]}
                onChange={(event) => setOiThresholdValues((previous) => ({ ...previous, [oiThresholdUnit]: event.target.value }))}
                aria-invalid={!isThresholdValid}
                aria-describedby="oi-threshold-help"
              />
              <p id="oi-threshold-help" className="text-xs text-muted-foreground">
                {oiThresholdUnit === 'percent'
                  ? 'Percentage change from the earlier OI value over the selected OI period.'
                  : oiThresholdUnit === 'usdt'
                    ? 'Change in coin OI × the average of the earlier and latest prices over the selected OI period.'
                    : 'Change in the number of coins held in open positions over the selected OI period.'}
              </p>
              {!isThresholdValid && <p className="text-xs text-destructive">Enter a threshold greater than zero.</p>}
            </div>

            <div className="space-y-2">
              <Label>Alert Direction</Label>
              <Select value={oiDirection} onValueChange={(v) => setOiDirection(v as 'increase' | 'decrease' | 'both')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Both (Increase & Decrease)</SelectItem>
                  <SelectItem value="increase">Increase Only</SelectItem>
                  <SelectItem value="decrease">Decrease Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="oi-period">OI Period</Label>
                <Select value={oiPeriod} onValueChange={setOiPeriod}>
                  <SelectTrigger id="oi-period">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {OI_PERIOD_OPTIONS.map((p) => (
                      <SelectItem key={p} value={p}>{p === 'all' ? 'All periods' : p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Volume Period</Label>
                <Select value={volumePeriod} onValueChange={setVolumePeriod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VOLUME_PERIOD_OPTIONS.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="min-volume">Min Period Volume (USDT)</Label>
              <Input
                id="min-volume"
                type="number"
                placeholder="0"
                value={minVolumeUsdt}
                onChange={(e) => setMinVolumeUsdt(e.target.value)}
              />
            </div>
          </div>

          <Separator />

          {/* Notifications */}
          <div className="space-y-4">
            <Label className="text-base">Notifications</Label>

            <div className="flex items-center justify-between">
              <Label htmlFor="enable-telegram">Telegram</Label>
              <Switch
                id="enable-telegram"
                checked={enableTelegram}
                onCheckedChange={setEnableTelegram}
              />
            </div>

            {enableTelegram && (
              <div className="pl-4">
                <div className="space-y-2">
                  <Label htmlFor="telegram-destination">Destination</Label>
                  <TelegramDestinationSelect
                    id="telegram-destination"
                    chatId={telegramChatId}
                    replyToMessageId={telegramReplyTo}
                    onChange={({ chat_id, reply_to_message_id }) => {
                      setTelegramChatId(chat_id)
                      setTelegramReplyTo(reply_to_message_id ? String(reply_to_message_id) : '')
                    }}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <Label htmlFor="enable-pushover">Pushover</Label>
              <Switch
                id="enable-pushover"
                checked={enablePushover}
                onCheckedChange={setEnablePushover}
              />
            </div>
          </div>

          <Separator />

          {/* Tags */}
          <div className="space-y-4">
            <TagExpressionInput
              value={tagExpression}
              onChange={setTagExpression}
              legacyRequired={legacyRequired}
              legacyExcluded={legacyExcluded}
            />
          </div>

          <Separator />

          {/* Ignore List */}
          <div className="space-y-2">
            <Label htmlFor="ignore-symbols">Ignore Symbols</Label>
            <Input
              id="ignore-symbols"
              placeholder="BTC, ETH"
              value={ignoreSymbols}
              onChange={(e) => setIgnoreSymbols(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Symbols to ignore for this alert
            </p>
          </div>
        </div>

        <SheetFooter className="gap-2 sm:gap-0">
          {!isNew && onDelete && (
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting || isSaving}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          )}
          <div className="flex-1" />
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving || isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || isDeleting || !name.trim() || exchanges.length === 0 || !isThresholdValid}
          >
            <Save className="mr-2 h-4 w-4" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
